import React from 'react';

import {
  TpHoverInfo,
  TpHoverRenderContext,
  TpHoverRenderInfo,
  ZoomPanCanvas,
} from '../../zoompan-canvas/ZoomPanCanvas';
import { TpZoomPanelInvPointMapInfo } from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpChartProps } from '../interfaces';
import { createDefinition } from './dataMatrix.Renderer';
import { convertColumnDataToDisplayString } from '../../helpers/misc';
import { TpRectangleSelection } from '../../zoompan-canvas/helpers';

export function DataMatrix({ chartTypeManifest, dataContext, dataBinding, config, widgetContext }: TpChartProps) {
  const widgetGlobalConfig = widgetContext.dashboardContext.widgetGlobalConfig ?? {};
  const {
    definition,
    areaMatrix,
    dataTable,
    dataRowItemIds,
    dataHoverLabelRow,
    hoverLabelRowInfo,
    hoverLabelColumnInfo,
    dataHoverLabelcolumn,
    matrixColumns,
    matrixColumnsData,
    rowSortIndex,
    colSortIndex,
    rowSortInvIndex,
    colSortInvIndex,
    getItemIndexById,
    title,
  } = createDefinition(chartTypeManifest, dataContext, dataBinding, config, widgetGlobalConfig);

  const rowCount = dataRowItemIds.length;
  const colCount = matrixColumns.length;

  function getHoverInfo(
    mappingInfo: TpZoomPanelInvPointMapInfo,
    logicPositX: number,
    logicPositY: number,
    renderPositX: number,
    renderPositY: number,
  ): TpHoverInfo | null {
    if (mappingInfo.area?.id != 'matrix') return null;

    const rowIdx = Math.floor(logicPositY * dataRowItemIds.length);
    const colIdx = Math.floor(logicPositX * matrixColumns.length);
    if (rowIdx >= 0 && rowIdx < dataRowItemIds.length && colIdx >= 0 && colIdx < matrixColumns.length)
      return {
        dataTableId: dataBinding.dataTableId,
        itemId: dataRowItemIds[rowSortIndex[rowIdx]],
        secondDimension: {
          dataTableId: definition.dataTableColumns.id,
          itemId: matrixColumns[colSortIndex[colIdx]].columnItemId!,
        },
      };
    return null;
  }

  function getHoverRenderInfo(hoverInfo: TpHoverInfo, context: TpHoverRenderContext): TpHoverRenderInfo | null {
    if (hoverInfo.dataTableId != dataBinding.dataTableId || hoverInfo.catColumnId) return null;
    const hoverRowItemNr = getItemIndexById(hoverInfo.itemId);
    let hoverColItemNr = null;
    if (hoverInfo.secondDimension?.itemId) {
      const foundIndex = matrixColumns.findIndex((col) => col.columnItemId == hoverInfo.secondDimension!.itemId);
      if (foundIndex >= 0) hoverColItemNr = foundIndex;
    }
    const labels: string[] = [];
    if (context.isActiveChart) {
      labels.push(convertColumnDataToDisplayString(hoverLabelRowInfo, dataHoverLabelRow[hoverRowItemNr]));
    }
    let logicPosX = 0;
    if (hoverColItemNr !== null) {
      const colInfo = matrixColumns[hoverColItemNr];
      if (dataHoverLabelcolumn)
        labels.push(convertColumnDataToDisplayString(hoverLabelColumnInfo, dataHoverLabelcolumn[hoverColItemNr]));
      logicPosX = (colSortInvIndex[hoverColItemNr] + 0.5) / colCount;
      labels.push(`Value=${convertColumnDataToDisplayString(colInfo, matrixColumnsData[colInfo.id][hoverRowItemNr])}`);
    }
    return {
      area: areaMatrix,
      logicPosX,
      logicPosY: (rowSortInvIndex[hoverRowItemNr] + 0.5) / rowCount,
      labels,
    };
  }

  function onRectangleSelection(rectangleSelection: TpRectangleSelection) {}

  function onItemClicked(itemId: string) {
    widgetContext.handleOpenItem(dataTable.canonicalTypeId, itemId);
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ZoomPanCanvas
        definition={definition}
        getHoverInfo={getHoverInfo}
        getHoverRenderInfo={getHoverRenderInfo}
        onRectangleSelection={onRectangleSelection}
        onItemClicked={onItemClicked}
        maxZoomXAbsolute={colCount * 20}
        maxZoomYAbsolute={rowCount * 20}
        widgetContext={widgetContext}
        chartTitleInfo={{
          assetId: dataContext.assetId,
          dataType: chartTypeManifest.id,
          title,
        }}
      />
    </div>
  );
}
